import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaBullseye,
  FaCalendarAlt,
  FaChartLine,
  FaCheckCircle,
  FaChevronRight,
  FaCrown,
  FaFileDownload,
  FaFistRaised,
  FaMapMarkerAlt,
  FaMedal,
  FaRocket,
  FaShieldAlt,
  FaStar,
  FaTrophy,
  FaUserFriends,
  FaUsers,
} from 'react-icons/fa';
import { AppContext } from '../context/AppContext';
import api from '../api/axios';

const FALLBACK_EVENT_NAME = 'KEJUARAAN TAEKWONDO';
const FALLBACK_EVENT_DESCRIPTION = 'Sistem manajemen kompetisi Taekwondo terintegrasi.';
const FALLBACK_LOCATION = 'Lokasi Belum Ditentukan';
const FALLBACK_CATEGORIES = ['Kyorugi Prestasi', 'Kyorugi Festival', 'Poomsae Prestasi', 'Poomsae Festival'];

const heroImages = [
  {
    src: 'https://tniad.mil.id/konten/unggahan/2024/10/WhatsApp-Image-2024-10-16-at-07.29.47.jpeg',
    alt: 'Taekwondo kyorugi',
  },
  {
    src: 'https://img.antarafoto.com/cache/1200x800/2025/07/17/kejuaraan-taekwondo-internasional-2025-1j7sx-dom.jpg',
    alt: 'Taekwondo poomsae',
  },
];

const missionItems = [
  {
    icon: FaTrophy,
    title: 'Pengembangan Prestasi',
    desc: 'Mendorong pengembangan prestasi atlet Taekwondo melalui kompetisi yang tertata dan transparan.',
  },
  {
    icon: FaBullseye,
    title: 'Evaluasi Pelatihan',
    desc: 'Menjadi tolok ukur perkembangan latihan bagi club, pelatih, dan atlet peserta.',
  },
  {
    icon: FaUsers,
    title: 'Persahabatan',
    desc: 'Mempererat persaudaraan antar Taekwondoin dalam kompetisi yang sportif, tertib, dan profesional.',
  },
];

const formatDate = (dateString) => {
  if (!dateString) return 'Jadwal Belum Ditentukan';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return 'Jadwal Belum Ditentukan';

  return date.toLocaleDateString('id-ID', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  if (!amount) return 'Belum Ditentukan';

  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    maximumFractionDigits: 0,
  }).format(amount);
};

const getInitialCountdown = () => ({ days: 0, hours: 0, minutes: 0, seconds: 0 });

const HomePage = () => {
  const { settings, masterData, isLoading } = useContext(AppContext);
  const [timeLeft, setTimeLeft] = useState(getInitialCountdown);
  const [standings, setStandings] = useState({ prestasi: [], festival: [] });
  const [standingCategory, setStandingCategory] = useState('Prestasi');
  const [standingsLoading, setStandingsLoading] = useState(true);

  const backendUrl = process.env.REACT_APP_BACKEND || process.env.REACT_APP_BACKEND_URL || '';
  const eventName = settings?.event_name?.trim() || FALLBACK_EVENT_NAME;
  const eventDescription = settings?.event_description?.trim() || FALLBACK_EVENT_DESCRIPTION;
  const eventDateLabel = formatDate(settings?.event_date);
  const eventLocation = settings?.location?.trim() || FALLBACK_LOCATION;
  const registrationOpen = settings?.is_registration_open !== false;
  const publicLeaderboard = settings?.is_public_leaderboard !== false;
  const importantNotes = Array.isArray(settings?.important_notes)
    ? settings.important_notes.filter((note) => String(note || '').trim())
    : [];
  const proposalTitle = settings?.proposal_title?.trim() || 'Unduh Proposal';
  const registrationFeeLabel = formatCurrency(settings?.prices?.team_registration);
  const eventCategories = useMemo(() => {
    const divisions = Array.isArray(masterData?.divisions)
      ? masterData.divisions.map((division) => division?.name).filter(Boolean)
      : [];

    return divisions.length > 0 ? divisions.slice(0, 8) : FALLBACK_CATEGORIES;
  }, [masterData?.divisions]);

  useEffect(() => {
    const targetDate = settings?.event_date ? new Date(settings.event_date).getTime() : null;

    if (!targetDate || Number.isNaN(targetDate)) {
      setTimeLeft(getInitialCountdown());
      return undefined;
    }

    const updateCountdown = () => {
      const distance = targetDate - new Date().getTime();

      if (distance <= 0) {
        setTimeLeft(getInitialCountdown());
        return;
      }

      setTimeLeft({
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((distance % (1000 * 60)) / 1000),
      });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [settings?.event_date]);

  useEffect(() => {
    if (settings?.is_public_leaderboard === false) {
      setStandingsLoading(false);
      return undefined;
    }

    const fetchStandings = async () => {
      setStandingsLoading(true);
      try {
        const response = await api.get('/medals/standings');
        setStandings({
          prestasi: response.data.standings_prestasi || [],
          festival: response.data.standings_festival || [],
        });
      } catch (error) {
        console.error('Gagal memuat klasemen publik:', error);
        setStandings({ prestasi: [], festival: [] });
      } finally {
        setStandingsLoading(false);
      }
    };

    fetchStandings();
    return undefined;
  }, [settings?.is_public_leaderboard]);

  const topThree = useMemo(() => {
    const source = standingCategory === 'Prestasi' ? standings.prestasi : standings.festival;

    return source
      .map((item) => ({
        team: item.team_name,
        gold: Number(item.gold || 0),
        silver: Number(item.silver || 0),
        bronze: Number(item.bronze || 0),
        totalScore: Number(item.gold || 0) * 3 + Number(item.silver || 0) * 2 + Number(item.bronze || 0),
      }))
      .filter((item) => item.totalScore > 0)
      .sort((a, b) => b.totalScore - a.totalScore)
      .slice(0, 3);
  }, [standingCategory, standings]);

  const handleDownloadProposal = () => {
    if (!settings?.proposal_file) return;
    window.open(`${backendUrl}/uploads/proposal_file/${settings.proposal_file}`, '_blank', 'noopener,noreferrer');
  };

  if (isLoading) {
    return (
      <div className="sulteng-home-loader">
        <div className="spinner-border text-success" role="status" />
        <span>Memuat halaman...</span>
        <style>{`
          .sulteng-home-loader {
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.8rem;
            color: #14532d;
            font-weight: 800;
            background: #fef9c3;
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="sulteng-home">
      <div className="sulteng-home__pattern" />

      <header className="sulteng-hero">
        <div className="container position-relative">
          <div className="row align-items-center g-5">
            <div className="col-12 col-lg-6">
              <div className="sulteng-hero__content">
                <span className="sulteng-kicker">
                  <FaCalendarAlt /> {eventDateLabel} - {eventLocation}
                </span>
                <div className="sulteng-event-name">{eventName}</div>
                <h1>
                  <span>Bersama Menuju</span>
                  <strong>Puncak Prestasi</strong>
                </h1>
                <p>{eventDescription}</p>

                <div className="d-flex flex-wrap gap-3 mb-4">
                  {registrationOpen ? (
                    <Link to="/register" className="btn sulteng-btn-primary">
                      <FaRocket /> Daftar Sekarang
                    </Link>
                  ) : (
                    <button type="button" className="btn sulteng-btn-muted" disabled>
                      <FaRocket /> Pendaftaran Ditutup
                    </button>
                  )}

                  {settings?.proposal_file && (
                    <button type="button" className="btn sulteng-btn-light" onClick={handleDownloadProposal}>
                      <FaFileDownload /> {proposalTitle}
                    </button>
                  )}
                </div>

                <div className="sulteng-countdown">
                  <div className="sulteng-countdown__head">
                    <FaChartLine />
                    <span>Menuju Pelaksanaan</span>
                  </div>
                  <div className="row g-2 g-sm-3">
                    {[
                      { label: 'Hari', value: timeLeft.days },
                      { label: 'Jam', value: timeLeft.hours },
                      { label: 'Menit', value: timeLeft.minutes },
                      { label: 'Detik', value: timeLeft.seconds },
                    ].map((item) => (
                      <div className="col-3" key={item.label}>
                        <div className="sulteng-countdown__box">
                          <strong>{String(item.value).padStart(2, '0')}</strong>
                          <small>{item.label}</small>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="sulteng-countdown__range">{eventDateLabel}</div>
                </div>
              </div>
            </div>

            <div className="col-12 col-lg-6">
              <div className="sulteng-hero__media">
                <img src={heroImages[0].src} alt={heroImages[0].alt} className="sulteng-hero__image main" />
                <img src={heroImages[1].src} alt={heroImages[1].alt} className="sulteng-hero__image secondary" />
                <div className="sulteng-hero__badge">
                  <FaShieldAlt />
                  <span>
                    <strong>PSS & DSS</strong>
                    <small>Standar internasional</small>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <section id="about" className="sulteng-section">
        <div className="container">
          <div className="sulteng-section__title text-center">
            <span>Visi & Misi</span>
            <h2>Kejuaraan yang Mengangkat Prestasi</h2>
          </div>
          <div className="row g-4">
            {missionItems.map((item) => {
              const Icon = item.icon;

              return (
                <div className="col-12 col-md-4" key={item.title}>
                  <article className="sulteng-mission-card h-100">
                    <div className="sulteng-mission-card__icon">
                      <Icon />
                    </div>
                    <h3>{item.title}</h3>
                    <p>{item.desc}</p>
                  </article>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="sulteng-patron">
        <div className="sulteng-patron__pattern" />
        <div className="container position-relative">
          <div className="sulteng-patron__card">
            <div className="sulteng-patron__photo-wrap">
              <img
                src={heroImages[0].src}
                alt={eventName}
                className="sulteng-patron__photo"
              />
            </div>
            <div className="sulteng-patron__content">
              <FaStar className="sulteng-patron__star" />
              <FaStar className="sulteng-patron__star" />
              <span>Informasi Event</span>
              <h2>{eventName}</h2>
              <p>{importantNotes[0] || eventDescription}</p>
            </div>
          </div>
        </div>
      </section>

      <section id="details" className="sulteng-info-block">
        <div className="container">
          <div className="row g-4 g-lg-5">
            <div className="col-12 col-lg-6">
              <div className="sulteng-section__title">
                <span>Informasi Event</span>
                <h2>Detail Kejuaraan</h2>
              </div>

              <div className="sulteng-detail-list">
                {[
                  { icon: FaCalendarAlt, label: 'Tanggal Pelaksanaan', value: formatDate(settings?.event_date) },
                  { icon: FaMapMarkerAlt, label: 'Lokasi Pertandingan', value: eventLocation },
                  { icon: FaCheckCircle, label: 'Status Pendaftaran', value: registrationOpen ? 'Dibuka' : 'Ditutup' },
                ].map((item) => {
                  const Icon = item.icon;
                  return (
                    <div className="sulteng-detail-card" key={item.label}>
                      <span><Icon /></span>
                      <div>
                        <small>{item.label}</small>
                        <strong>{item.value}</strong>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="sulteng-prize">
                <FaMedal />
                <small>Biaya Pendaftaran Tim</small>
                <strong>{registrationFeeLabel}</strong>
              </div>
            </div>

            <div className="col-12 col-lg-6">
              <div className="sulteng-category-card" id="categories">
                <div className="d-flex align-items-center gap-3 mb-4">
                  <div className="sulteng-category-card__icon">
                    <FaFistRaised />
                  </div>
                  <div>
                    <span>Kategori Event</span>
                    <h2>Kelas Pertandingan</h2>
                  </div>
                </div>

                <div className="row g-3">
                  {eventCategories.map((category) => (
                    <div className="col-12 col-sm-6" key={category}>
                      <div className="sulteng-category-item">
                        <FaChevronRight />
                        <strong>{category}</strong>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="sulteng-note">
                  {importantNotes[1] || 'Kategori dan divisi mengikuti pengaturan data master yang ditentukan admin.'}
                </div>

                {masterData?.divisions?.length > 0 && (
                  <div className="sulteng-division-mini">
                    <h3>Divisi Aktif</h3>
                    <div className="d-flex flex-wrap gap-2">
                      {masterData.divisions.slice(0, 8).map((division) => (
                        <span key={division.name}>{division.name}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {publicLeaderboard && (
        <section className="sulteng-standings">
          <div className="container">
            <div className="sulteng-section__title text-center">
              <span><FaTrophy /> Hall of Fame</span>
              <h2>Klasemen Juara Umum</h2>
            </div>

            <div className="d-flex justify-content-center mb-4">
              <div className="sulteng-segment">
                {['Prestasi', 'Festival'].map((item) => (
                  <button
                    type="button"
                    key={item}
                    className={standingCategory === item ? 'active' : ''}
                    onClick={() => setStandingCategory(item)}
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>

            {standingsLoading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-success" role="status" />
              </div>
            ) : topThree.length > 0 ? (
              <div className="row g-4 align-items-end justify-content-center">
                {[topThree[1], topThree[0], topThree[2]].map((team, index) => {
                  if (!team) return null;
                  const rank = index === 1 ? 1 : index === 0 ? 2 : 3;
                  return (
                    <div className={`col-12 col-md-4 ${rank === 1 ? 'order-md-2' : rank === 2 ? 'order-md-1' : 'order-md-3'}`} key={team.team}>
                      <article className={`sulteng-podium ${rank === 1 ? 'champion' : ''}`}>
                        {rank === 1 ? <FaCrown /> : <FaMedal />}
                        <small>{rank === 1 ? 'Juara Umum' : `Peringkat ${rank}`}</small>
                        <h3>{team.team}</h3>
                        <div className="sulteng-podium__medals">
                          <span>Emas {team.gold}</span>
                          <span>Perak {team.silver}</span>
                          <span>Perunggu {team.bronze}</span>
                        </div>
                      </article>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="sulteng-empty">
                <FaTrophy />
                <h3>Belum Ada Perolehan Medali</h3>
                <p>Klasemen {standingCategory} akan muncul setelah hasil pertandingan diinput.</p>
              </div>
            )}
          </div>
        </section>
      )}

      <section className="sulteng-gallery">
        <div className="container">
          <div className="sulteng-section__title text-center">
            <span>Semangat Olimpiade</span>
            <h2>Bawa Energi Sang Juara ke Arena</h2>
          </div>
          <div className="row g-4">
            {[
              {
                title: 'Kyorugi Action',
                src: 'https://cdn.antaranews.com/cache/asiangames/800x533/2018/8/21/Taekwondo-kyorugi-pi-final-Korea-Selatan-Kazakhstan-210818-dar-7.JPG',
              },
              {
                title: 'Atmosfer Arena',
                src: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcR91bqF-JdMSNVy8Q3k39_cSrskGGEw9cpxuQ&s',
              },
            ].map((item) => (
              <div className="col-12 col-lg-6" key={item.title}>
                <article className="sulteng-gallery-card">
                  <img src={item.src} alt={item.title} />
                  <div>
                    <FaUserFriends />
                    <h3>{item.title}</h3>
                  </div>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="sulteng-final-cta">
        <div className="container">
          <div className="sulteng-final-cta__box text-center">
            <FaShieldAlt />
            <h2>
              Daftarkan Club dan Atlet Terbaik Anda
            </h2>
            <p>
              {registrationOpen
                ? `Pendaftaran ${eventName} sedang dibuka. Gunakan portal resmi untuk mengelola data tim, atlet, pembayaran, dan dokumen pendukung.`
                : `Pendaftaran ${eventName} sedang ditutup. Pantau informasi resmi panitia untuk pembaruan berikutnya.`}
            </p>
            {registrationOpen ? (
              <Link to="/register" className="btn sulteng-btn-primary">
                Menuju Portal Pendaftaran <FaChevronRight />
              </Link>
            ) : (
              <button type="button" className="btn sulteng-btn-muted" disabled>
                Pendaftaran Ditutup
              </button>
            )}
          </div>
        </div>
      </section>

      <style>{`
        .sulteng-home {
          position: relative;
          overflow: hidden;
          min-height: 100vh;
          color: #0f172a;
          background: #facc15;
        }

        .sulteng-home__pattern,
        .sulteng-patron__pattern {
          position: absolute;
          inset: 0;
          opacity: 0.06;
          background-image: radial-gradient(#166534 10%, transparent 10%), radial-gradient(#166534 10%, transparent 10%);
          background-size: 40px 40px;
          background-position: 0 0, 20px 20px;
          pointer-events: none;
        }

        .sulteng-hero {
          position: relative;
          padding: 8.5rem 0 5rem;
          background: #ffffff;
          border-bottom-left-radius: 3rem;
          border-bottom-right-radius: 3rem;
          z-index: 1;
        }

        .sulteng-hero:after {
          content: "";
          position: absolute;
          top: 0;
          right: 0;
          width: 45%;
          height: 100%;
          background: rgba(22, 101, 52, 0.06);
          border-bottom-left-radius: 11rem;
          pointer-events: none;
        }

        .sulteng-hero__content {
          position: relative;
          z-index: 2;
          animation: sultengSlideIn 0.7s ease-out;
        }

        .sulteng-kicker,
        .sulteng-section__title span {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 1rem;
          border-radius: 999px;
          color: #9a3412;
          background: #ffedd5;
          border: 1px solid #fed7aa;
          font-size: 0.76rem;
          font-weight: 900;
          text-transform: uppercase;
          padding: 0.55rem 0.85rem;
        }

        .sulteng-event-name {
          margin-bottom: 0.8rem;
          color: #14532d;
          font-size: 1.05rem;
          font-weight: 900;
          text-transform: uppercase;
        }

        .sulteng-hero h1 {
          margin: 0 0 1.35rem;
          font-size: 3.4rem;
          font-weight: 900;
          line-height: 0.98;
          text-transform: uppercase;
        }

        .sulteng-hero h1 span,
        .sulteng-hero h1 strong {
          display: block;
        }

        .sulteng-hero h1 span {
          color: #16a34a;
          text-shadow: 1px 1px 0 #166534, 2px 2px 0 #14532d, 4px 4px 12px rgba(0, 0, 0, 0.28);
        }

        .sulteng-hero h1 strong {
          color: #f97316;
          text-shadow: 1px 1px 0 #c2410c, 2px 2px 0 #9a3412, 4px 4px 12px rgba(0, 0, 0, 0.28);
        }

        .sulteng-hero p {
          max-width: 640px;
          margin-bottom: 1.7rem;
          padding: 0.9rem 1rem;
          border-left: 4px solid #22c55e;
          border-radius: 0 16px 16px 0;
          color: #475569;
          background: rgba(240, 253, 244, 0.78);
          font-weight: 600;
          line-height: 1.75;
        }

        .sulteng-btn-primary,
        .sulteng-btn-light,
        .sulteng-btn-muted {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.55rem;
          border-radius: 999px;
          font-weight: 900;
          padding: 0.9rem 1.25rem;
        }

        .sulteng-btn-primary {
          color: #ffffff;
          background: linear-gradient(135deg, #f97316, #ea580c);
          box-shadow: 0 18px 38px rgba(234, 88, 12, 0.28);
        }

        .sulteng-btn-primary:hover {
          color: #ffffff;
          transform: translateY(-2px);
          box-shadow: 0 24px 46px rgba(234, 88, 12, 0.35);
        }

        .sulteng-btn-light {
          color: #14532d;
          background: #ffffff;
          border: 1px solid rgba(22, 101, 52, 0.15);
          box-shadow: 0 14px 30px rgba(15, 23, 42, 0.08);
        }

        .sulteng-btn-light:hover {
          color: #ffffff;
          background: #14532d;
        }

        .sulteng-btn-muted {
          color: #ffffff;
          background: #64748b;
          cursor: not-allowed;
        }

        .sulteng-countdown {
          position: relative;
          max-width: 620px;
          overflow: hidden;
          padding: 1.25rem;
          border: 2px solid rgba(250, 204, 21, 0.55);
          border-radius: 26px;
          color: #ffffff;
          background: linear-gradient(135deg, #14532d, #166534);
          box-shadow: 0 26px 55px rgba(20, 83, 45, 0.28);
        }

        .sulteng-countdown:after {
          content: "";
          position: absolute;
          top: -4rem;
          right: -4rem;
          width: 12rem;
          height: 12rem;
          background: rgba(34, 197, 94, 0.38);
          filter: blur(28px);
          border-radius: 999px;
        }

        .sulteng-countdown__head {
          position: relative;
          z-index: 1;
          display: flex;
          align-items: center;
          gap: 0.55rem;
          margin-bottom: 1rem;
          color: #facc15;
          font-weight: 900;
          text-transform: uppercase;
        }

        .sulteng-countdown__box {
          position: relative;
          z-index: 1;
          text-align: center;
          padding: 0.95rem 0.45rem;
          border: 1px solid rgba(255, 255, 255, 0.18);
          border-radius: 18px;
          background: rgba(255, 255, 255, 0.1);
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.12);
          backdrop-filter: blur(10px);
        }

        .sulteng-countdown__box strong {
          display: block;
          font-size: 1.7rem;
          font-weight: 900;
          line-height: 1;
        }

        .sulteng-countdown__box small {
          display: block;
          margin-top: 0.4rem;
          color: #86efac;
          font-size: 0.68rem;
          font-weight: 900;
          text-transform: uppercase;
        }

        .sulteng-countdown__range {
          position: relative;
          z-index: 1;
          margin-top: 1rem;
          padding: 0.6rem;
          border-radius: 999px;
          text-align: center;
          color: #bbf7d0;
          background: rgba(0, 0, 0, 0.18);
          font-size: 0.9rem;
          font-weight: 800;
        }

        .sulteng-hero__media {
          position: relative;
          z-index: 2;
          min-height: 470px;
          animation: sultengFloat 4s ease-in-out infinite;
        }

        .sulteng-hero__image {
          position: absolute;
          object-fit: cover;
          border: 4px solid #ffffff;
          border-radius: 28px;
          box-shadow: 0 28px 60px rgba(15, 23, 42, 0.22);
        }

        .sulteng-hero__image.main {
          top: 1.5rem;
          right: 0;
          width: 78%;
          height: 72%;
        }

        .sulteng-hero__image.secondary {
          left: 0;
          bottom: 0;
          z-index: -1;
          width: 62%;
          height: 58%;
          border-color: #facc15;
        }

        .sulteng-hero__badge {
          position: absolute;
          left: 8%;
          top: 2%;
          display: inline-flex;
          align-items: center;
          gap: 0.65rem;
          padding: 0.9rem 1rem;
          border-radius: 18px;
          color: #14532d;
          background: rgba(255, 255, 255, 0.92);
          box-shadow: 0 18px 40px rgba(15, 23, 42, 0.16);
          backdrop-filter: blur(14px);
        }

        .sulteng-hero__badge svg {
          color: #f97316;
          font-size: 1.25rem;
        }

        .sulteng-hero__badge span {
          display: flex;
          flex-direction: column;
          line-height: 1.05;
        }

        .sulteng-hero__badge strong {
          font-weight: 900;
        }

        .sulteng-hero__badge small {
          color: #64748b;
          font-size: 0.72rem;
          font-weight: 800;
        }

        .sulteng-section {
          position: relative;
          z-index: 1;
          padding: 5rem 0;
        }

        .sulteng-section__title {
          margin-bottom: 2.3rem;
        }

        .sulteng-section__title h2 {
          margin: 0;
          color: #14532d;
          font-size: 2.35rem;
          font-weight: 900;
          line-height: 1.08;
          text-transform: uppercase;
        }

        .sulteng-mission-card,
        .sulteng-category-card,
        .sulteng-podium,
        .sulteng-final-cta__box {
          position: relative;
          overflow: hidden;
          border: 1px solid rgba(22, 101, 52, 0.12);
          border-radius: 26px;
          background: rgba(255, 255, 255, 0.92);
          box-shadow: 0 20px 45px rgba(15, 23, 42, 0.1);
          backdrop-filter: blur(14px);
        }

        .sulteng-mission-card {
          padding: 2rem;
          transition: transform 0.25s ease, box-shadow 0.25s ease;
        }

        .sulteng-mission-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 28px 60px rgba(15, 23, 42, 0.14);
        }

        .sulteng-mission-card__icon {
          width: 64px;
          height: 64px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 1.25rem;
          border-radius: 20px;
          color: #166534;
          background: #f0fdf4;
          font-size: 1.65rem;
        }

        .sulteng-mission-card h3 {
          color: #14532d;
          font-size: 1.05rem;
          font-weight: 900;
          text-transform: uppercase;
        }

        .sulteng-mission-card p {
          margin: 0;
          color: #475569;
          font-weight: 600;
          line-height: 1.75;
        }

        .sulteng-patron {
          position: relative;
          overflow: hidden;
          padding: 5rem 0;
          color: #ffffff;
          background: linear-gradient(180deg, #14532d 0%, #052e16 100%);
          border-top-left-radius: 3rem;
          border-top-right-radius: 3rem;
        }

        .sulteng-patron__card {
          position: relative;
          display: grid;
          grid-template-columns: minmax(240px, 340px) 1fr;
          align-items: center;
          gap: 2rem;
          max-width: 980px;
          margin: 0 auto;
          padding: 1.4rem;
          border: 1px solid rgba(250, 204, 21, 0.22);
          border-radius: 30px;
          background: rgba(255, 255, 255, 0.08);
          box-shadow: 0 28px 70px rgba(0, 0, 0, 0.24);
          backdrop-filter: blur(14px);
        }

        .sulteng-patron__photo-wrap {
          position: relative;
          overflow: hidden;
          aspect-ratio: 4 / 5;
          border: 4px solid rgba(250, 204, 21, 0.9);
          border-radius: 26px;
          background: #ffffff;
          box-shadow: 0 24px 55px rgba(0, 0, 0, 0.24);
        }

        .sulteng-patron__photo {
          width: 100%;
          height: 100%;
          display: block;
          object-fit: cover;
          object-position: center top;
        }

        .sulteng-patron__content {
          text-align: left;
        }

        .sulteng-patron__star {
          color: #facc15;
          font-size: 2.5rem;
          margin-bottom: 0.9rem;
          animation: sultengSpin 8s linear infinite;
        }

        .sulteng-patron span {
          display: block;
          color: #86efac;
          font-weight: 900;
          text-transform: uppercase;
        }

        .sulteng-patron h2 {
          max-width: 680px;
          margin: 0.8rem 0;
          color: #facc15;
          font-size: 2.7rem;
          font-weight: 900;
          line-height: 1.05;
        }

        .sulteng-patron p {
          display: inline-block;
          margin: 0;
          padding: 0.8rem 1.2rem;
          border: 1px solid rgba(134, 239, 172, 0.25);
          border-radius: 999px;
          background: rgba(22, 101, 52, 0.76);
          color: #ffffff;
          font-weight: 800;
        }

        .sulteng-info-block,
        .sulteng-standings,
        .sulteng-final-cta {
          position: relative;
          z-index: 1;
          padding: 5rem 0;
          background: #f8fafc;
        }

        .sulteng-detail-list {
          display: grid;
          gap: 1rem;
        }

        .sulteng-detail-card {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem;
          border: 1px solid #e2e8f0;
          border-radius: 20px;
          background: #ffffff;
          box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
        }

        .sulteng-detail-card span {
          width: 58px;
          height: 58px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 auto;
          border-radius: 18px;
          color: #ea580c;
          background: #ffedd5;
          font-size: 1.45rem;
        }

        .sulteng-detail-card small {
          display: block;
          color: #64748b;
          font-size: 0.72rem;
          font-weight: 900;
          text-transform: uppercase;
        }

        .sulteng-detail-card strong {
          display: block;
          color: #14532d;
          font-size: 1.05rem;
          font-weight: 900;
          line-height: 1.35;
        }

        .sulteng-prize {
          position: relative;
          overflow: hidden;
          margin-top: 1.5rem;
          padding: 2rem;
          border-radius: 26px;
          color: #ffffff;
          background: linear-gradient(135deg, #facc15, #f97316);
          box-shadow: 0 22px 48px rgba(234, 88, 12, 0.25);
        }

        .sulteng-prize svg {
          position: absolute;
          top: -1.4rem;
          right: -1.2rem;
          color: rgba(255, 255, 255, 0.22);
          font-size: 9rem;
        }

        .sulteng-prize small,
        .sulteng-prize strong {
          position: relative;
          z-index: 1;
          display: block;
        }

        .sulteng-prize small {
          color: #78350f;
          font-weight: 900;
          text-transform: uppercase;
        }

        .sulteng-prize strong {
          margin-top: 0.2rem;
          font-size: 2.8rem;
          font-weight: 900;
          line-height: 1;
          text-shadow: 0 4px 14px rgba(0, 0, 0, 0.2);
        }

        .sulteng-category-card {
          padding: 2rem;
        }

        .sulteng-category-card__icon {
          width: 58px;
          height: 58px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 18px;
          color: #166534;
          background: #dcfce7;
          font-size: 1.4rem;
        }

        .sulteng-category-card span {
          display: block;
          color: #ea580c;
          font-size: 0.74rem;
          font-weight: 900;
          text-transform: uppercase;
        }

        .sulteng-category-card h2 {
          margin: 0;
          color: #14532d;
          font-size: 1.7rem;
          font-weight: 900;
          text-transform: uppercase;
        }

        .sulteng-category-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.95rem;
          border: 1px solid #e2e8f0;
          border-radius: 18px;
          background: #f8fafc;
          transition: background 0.2s ease, border-color 0.2s ease;
        }

        .sulteng-category-item:hover {
          border-color: #bbf7d0;
          background: #f0fdf4;
        }

        .sulteng-category-item svg {
          color: #16a34a;
        }

        .sulteng-category-item strong {
          color: #334155;
          font-weight: 900;
        }

        .sulteng-note,
        .sulteng-division-mini {
          margin-top: 1rem;
          padding: 1rem;
          border-radius: 18px;
          color: #14532d;
          background: #f0fdf4;
          font-weight: 700;
          line-height: 1.65;
        }

        .sulteng-division-mini h3 {
          margin-bottom: 0.75rem;
          font-size: 0.9rem;
          font-weight: 900;
          text-transform: uppercase;
        }

        .sulteng-division-mini span {
          display: inline-flex;
          border-radius: 999px;
          padding: 0.45rem 0.7rem;
          color: #14532d;
          background: #ffffff;
          font-size: 0.78rem;
          font-weight: 900;
          text-transform: none;
        }

        .sulteng-segment {
          display: inline-flex;
          padding: 0.35rem;
          border: 1px solid #e2e8f0;
          border-radius: 999px;
          background: #ffffff;
          box-shadow: 0 12px 28px rgba(15, 23, 42, 0.06);
        }

        .sulteng-segment button {
          border: 0;
          border-radius: 999px;
          padding: 0.7rem 1rem;
          color: #64748b;
          background: transparent;
          font-weight: 900;
        }

        .sulteng-segment button.active {
          color: #ffffff;
          background: #166534;
          box-shadow: 0 12px 24px rgba(22, 101, 52, 0.22);
        }

        .sulteng-podium {
          padding: 2rem;
          text-align: center;
        }

        .sulteng-podium.champion {
          border-color: rgba(250, 204, 21, 0.8);
          background: linear-gradient(180deg, #ffffff 0%, #fffbeb 100%);
          transform: translateY(-10px);
        }

        .sulteng-podium svg {
          color: #f59e0b;
          font-size: 3rem;
          margin-bottom: 0.8rem;
        }

        .sulteng-podium small {
          display: block;
          color: #ea580c;
          font-weight: 900;
          text-transform: uppercase;
        }

        .sulteng-podium h3 {
          margin: 0.6rem 0 1rem;
          color: #14532d;
          font-size: 1.25rem;
          font-weight: 900;
          text-transform: uppercase;
        }

        .sulteng-podium__medals {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          overflow: hidden;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          background: #ffffff;
        }

        .sulteng-podium__medals span {
          padding: 0.65rem 0.35rem;
          color: #475569;
          font-size: 0.75rem;
          font-weight: 900;
        }

        .sulteng-podium__medals span + span {
          border-left: 1px solid #e2e8f0;
        }

        .sulteng-empty {
          max-width: 540px;
          margin: 0 auto;
          text-align: center;
          padding: 3rem 1rem;
          color: #64748b;
        }

        .sulteng-empty svg {
          color: rgba(100, 116, 139, 0.28);
          font-size: 4rem;
          margin-bottom: 1rem;
        }

        .sulteng-empty h3 {
          color: #334155;
          font-weight: 900;
        }

        .sulteng-gallery {
          position: relative;
          z-index: 1;
          padding: 5rem 0;
          background: #facc15;
          border-top-left-radius: 3rem;
          border-top-right-radius: 3rem;
        }

        .sulteng-gallery-card {
          position: relative;
          overflow: hidden;
          min-height: 290px;
          border: 4px solid #ffffff;
          border-radius: 28px;
          box-shadow: 0 25px 55px rgba(15, 23, 42, 0.18);
        }

        .sulteng-gallery-card img {
          width: 100%;
          height: 290px;
          object-fit: fill;
          transition: transform 0.55s ease;
        }

        .sulteng-gallery-card:hover img {
          transform: scale(1.06);
        }

        .sulteng-gallery-card div {
          position: absolute;
          inset: auto 0 0;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 1.5rem;
          color: #ffffff;
          background: linear-gradient(0deg, rgba(5, 46, 22, 0.92), rgba(5, 46, 22, 0));
        }

        .sulteng-gallery-card h3 {
          margin: 0;
          font-size: 1.3rem;
          font-weight: 900;
          text-transform: uppercase;
        }

        .sulteng-final-cta__box {
          padding: 3rem 1.4rem;
          color: #ffffff;
          background: linear-gradient(135deg, #14532d, #052e16);
        }

        .sulteng-final-cta__box > svg {
          color: #facc15;
          font-size: 3.5rem;
          margin-bottom: 1rem;
        }

        .sulteng-final-cta__box h2 {
          max-width: 760px;
          margin: 0 auto 1rem;
          font-size: 2.2rem;
          font-weight: 900;
          text-transform: uppercase;
          line-height: 1.1;
        }

        .sulteng-final-cta__box p {
          max-width: 700px;
          margin: 0 auto 1.6rem;
          color: #bbf7d0;
          font-weight: 650;
          line-height: 1.75;
        }

        @keyframes sultengFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-12px); }
        }

        @keyframes sultengSlideIn {
          from { opacity: 0; transform: translateX(35px); }
          to { opacity: 1; transform: translateX(0); }
        }

        @keyframes sultengSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @media (max-width: 991.98px) {
          .sulteng-hero {
            padding-top: 7.5rem;
          }

          .sulteng-hero:after {
            width: 100%;
            height: 38%;
            border-bottom-left-radius: 4rem;
          }

          .sulteng-hero h1 {
            font-size: 2.55rem;
          }

          .sulteng-hero__media {
            min-height: 400px;
          }

          .sulteng-patron__card {
            grid-template-columns: 1fr;
            max-width: 620px;
            text-align: center;
          }

          .sulteng-patron__photo-wrap {
            max-width: 320px;
            width: 100%;
            margin: 0 auto;
          }

          .sulteng-patron__content {
            text-align: center;
          }

          .sulteng-patron h2 {
            margin-left: auto;
            margin-right: auto;
          }

          .sulteng-podium.champion {
            transform: none;
          }
        }

        @media (max-width: 575.98px) {
          .sulteng-hero,
          .sulteng-section,
          .sulteng-patron,
          .sulteng-info-block,
          .sulteng-standings,
          .sulteng-gallery,
          .sulteng-final-cta {
            padding-left: 0;
            padding-right: 0;
          }

          .sulteng-hero h1 {
            font-size: 2.05rem;
          }

          .sulteng-section__title h2,
          .sulteng-patron h2,
          .sulteng-final-cta__box h2 {
            font-size: 1.75rem;
          }

          .sulteng-patron__card {
            gap: 1.25rem;
            padding: 1rem;
            border-radius: 24px;
          }

          .sulteng-patron__photo-wrap {
            max-width: 260px;
            border-radius: 22px;
          }

          .sulteng-hero__media {
            min-height: 320px;
          }

          .sulteng-hero__image.main {
            width: 86%;
            height: 68%;
          }

          .sulteng-hero__image.secondary {
            width: 72%;
            height: 52%;
          }

          .sulteng-countdown__box strong {
            font-size: 1.15rem;
          }

          .sulteng-countdown__box small {
            font-size: 0.58rem;
          }

          .sulteng-prize strong {
            font-size: 2rem;
          }
        }
      `}</style>
    </div>
  );
};

export default HomePage;
